<template>
  <v-dialog v-model="dialog" max-width="960" scrollable>
    <v-card :loading="isLoading">
      <v-card-title class="headline">
        {{ $t("STOCK.INVENTORY.REQUIREMENTS_LIST_TITLE") }}
      </v-card-title>
      <v-card-subtitle v-if="productLabel" class="pb-0">
        {{ productLabel }}
        <span v-if="warehouseLabel" class="grey--text"> · {{ warehouseLabel }}</span>
        <span v-if="asOfDate" class="grey--text"> · {{ $t("STOCK.INVENTORY.AS_OF_DATE") }}: {{ asOfDate }}</span>
      </v-card-subtitle>
      <v-card-text>
        <v-alert v-if="errorMessage" type="error" dense text>{{ errorMessage }}</v-alert>
        <v-row v-if="summary" dense class="mb-3">
          <v-col cols="6" sm="3">
            <div class="caption grey--text">{{ $t("STOCK.INVENTORY.ON_HAND") }}</div>
            <div class="subtitle-1 font-weight-medium">{{ formatQty(summary.quantity_on_hand) }}</div>
          </v-col>
          <v-col cols="6" sm="3">
            <v-tooltip bottom max-width="280">
              <template v-slot:activator="{ on, attrs }">
                <div v-bind="attrs" v-on="on">
                  <div class="caption stock-inventory-projected-label">{{ $t("STOCK.INVENTORY.PROJECTED") }}</div>
                  <div
                    class="subtitle-1 font-weight-medium stock-inventory-projected"
                    :class="{ 'stock-inventory-projected--negative': Number(summary.projected_quantity) < 0 }"
                  >
                    {{ formatQty(summary.projected_quantity) }}
                  </div>
                </div>
              </template>
              <span>{{ $t("STOCK.INVENTORY.PROJECTED_HINT") }}</span>
            </v-tooltip>
          </v-col>
          <v-col cols="6" sm="3">
            <div class="caption grey--text">{{ $t("STOCK.INVENTORY.PLANNED_IN") }}</div>
            <div class="subtitle-1 font-weight-medium">{{ formatQty(summary.planned_inbound_quantity) }}</div>
          </v-col>
          <v-col cols="6" sm="3">
            <div class="caption grey--text">{{ $t("STOCK.INVENTORY.PLANNED_OUT") }}</div>
            <div class="subtitle-1 font-weight-medium">{{ formatQty(summary.planned_outbound_quantity) }}</div>
          </v-col>
        </v-row>
        <v-simple-table v-if="rows.length" dense>
          <thead>
            <tr>
              <th>{{ $t("STOCK.INVENTORY.MRP_DATE") }}</th>
              <th>{{ $t("STOCK.INVENTORY.MRP_ELEMENT") }}</th>
              <th>{{ $t("STOCK.INVENTORY.MRP_REFERENCE") }}</th>
              <th class="text-right">{{ $t("STOCK.INVENTORY.MRP_QTY_CHANGE") }}</th>
              <th class="text-right">{{ $t("STOCK.INVENTORY.MRP_AVAILABLE") }}</th>
              <th>{{ $t("STOCK.WAREHOUSES.TITLE") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, index) in rows" :key="`mrp-${index}-${row.reference || row.mrp_element}`">
              <td>{{ row.date || "-" }}</td>
              <td>{{ mrpElementLabel(row.mrp_element) }}</td>
              <td>
                <router-link
                  v-if="row.reference_id && row.reference_type === 'purchase_order'"
                  :to="{ name: 'stock-purchase-orders-view', params: { id: row.reference_id } }"
                  target="_blank"
                >
                  {{ row.reference }}
                </router-link>
                <router-link
                  v-else-if="row.reference_id && row.reference_type === 'sales_order'"
                  :to="{ name: 'stock-sales-orders-view', params: { id: row.reference_id } }"
                  target="_blank"
                >
                  {{ row.reference }}
                </router-link>
                <span v-else>{{ row.reference || "-" }}</span>
              </td>
              <td class="text-right">
                <span v-if="row.quantity_change === null || row.quantity_change === undefined">-</span>
                <span v-else>{{ formatSignedQty(row.quantity_change) }}</span>
              </td>
              <td class="text-right">{{ formatQty(row.available_qty) }}</td>
              <td>{{ row.warehouse ? row.warehouse.name : "-" }}</td>
            </tr>
          </tbody>
        </v-simple-table>
        <v-alert v-else-if="!isLoading" type="info" dense text>
          {{ $t("STOCK.INVENTORY.NO_REQUIREMENTS") }}
        </v-alert>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text color="primary" @click="dialog = false">
          {{ $t("GENERAL.LABELS.CLOSE") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { GET_STOCK_INVENTORY_REQUIREMENTS_LIST } from "@/store/stock/stockInventory.module";
import { formatStockQuantity } from "@/common/stock/stockNumberFormat";

export default {
  name: "StockInventoryRequirementsListDialog",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    stockProductId: {
      type: Number,
      default: null,
    },
    asOfDate: {
      type: String,
      default: null,
    },
    productDisplayName: {
      type: String,
      default: "",
    },
    warehouseId: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      isLoading: false,
      errorMessage: null,
      rows: [],
      summary: null,
      productLabel: "",
      warehouseLabel: "",
    };
  },
  computed: {
    dialog: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
  },
  watch: {
    value(open) {
      if (open && this.stockProductId) {
        this.loadRequirements();
      }
    },
  },
  methods: {
    formatQty(value) {
      return formatStockQuantity(value);
    },
    formatSignedQty(value) {
      const qty = Number(value || 0);
      const formatted = this.formatQty(Math.abs(qty));
      if (qty > 0) {
        return `+${formatted}`;
      }
      if (qty < 0) {
        return `-${formatted}`;
      }
      return formatted;
    },
    mrpElementLabel(element) {
      const map = {
        stock: this.$t("STOCK.INVENTORY.MRP_STOCK"),
        po_item: this.$t("STOCK.INVENTORY.MRP_PO_ITEM"),
        so_item: this.$t("STOCK.INVENTORY.MRP_SO_ITEM"),
      };
      return map[element] || element;
    },
    loadRequirements() {
      this.isLoading = true;
      this.errorMessage = null;

      const params = {
        stock_product_id: this.stockProductId,
      };

      if (this.asOfDate) {
        params.as_of_date = this.asOfDate;
      }

      if (this.warehouseId) {
        params.warehouse_id = this.warehouseId;
      }

      return this.$store
        .dispatch(GET_STOCK_INVENTORY_REQUIREMENTS_LIST, params)
        .then((data) => {
          this.rows = data.rows || [];
          this.summary = data.summary || null;
          this.productLabel =
            (data.product && (data.product.display_name || data.product.code)) ||
            this.productDisplayName;
          this.warehouseLabel = data.warehouse_scope ? data.warehouse_scope.name : "";
        })
        .catch((errors) => {
          this.rows = [];
          this.summary = null;
          this.errorMessage =
            (errors && errors.message) ||
            (errors && errors.errors && Object.values(errors.errors).flat().join(" ")) ||
            this.$t("GENERAL.UNEXPECTED_ERROR");
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>

<style scoped>
.stock-inventory-projected-label {
  color: #283593;
  font-weight: 600;
}

.stock-inventory-projected {
  display: inline-block;
  padding: 1px 8px;
  border-radius: 10px;
  background: #e8eaf6;
  color: #283593;
  font-weight: 600;
}

.stock-inventory-projected--negative {
  background: #ffebee;
  color: #c62828;
}
</style>
